import { useState } from 'react';
import { Share2, MessageCircle, Mail, Link, Check } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Property } from '../types/property';
import { getContactEmail } from '@/config/runtime-config';

interface ShareButtonsProps {
  property: Property;
  agentPhone?: string;
  agentEmail?: string;
}

export function ShareButtons({ property, agentPhone, agentEmail }: ShareButtonsProps) {
  const [copiedLink, setCopiedLink] = useState(false);

  const propertyUrl = `${window.location.origin}/properties/${property.slug}`;
  const shareMessage = `Hi - here's a property I think you'll love: ${property.title}, ${property.address.suburb} - R${(property.price ?? 0).toLocaleString()}. Details: ${propertyUrl}`;

  const handleWhatsAppShare = () => {
    const phone = agentPhone || '+27615483584'; // fallback: Something Property WhatsApp
    const whatsappUrl = `https://wa.me/${phone.replace(/\s+/g, '')}?text=${encodeURIComponent(shareMessage)}`;
    window.open(whatsappUrl, '_blank');
    
    // Track share event
    console.log('WhatsApp share clicked', { propertyId: property.id, channel: 'whatsapp' });
  };

  const handleEmailShare = () => {
    const email = agentEmail || getContactEmail();
    const subject = encodeURIComponent(`Property Inquiry: ${property.title}`);
    const body = encodeURIComponent(`I'm interested in this property:\n\n${property.title}\n${property.address.suburb}, ${property.address.city}\nPrice: R${(property.price ?? 0).toLocaleString()}\n\nDetails: ${propertyUrl}\n\nPlease contact me with more information.`);
    
    const emailUrl = `mailto:${email}?subject=${subject}&body=${body}`;
    window.location.href = emailUrl;
    
    // Track share event
    console.log('Email share clicked', { propertyId: property.id, channel: 'email' });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(propertyUrl);
      setCopiedLink(true);
      toast.success('Link copied to clipboard!');
      
      setTimeout(() => setCopiedLink(false), 2000);
      
      // Track share event
      console.log('Link copied', { propertyId: property.id, channel: 'copy_link' });
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleGenericShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: property.title,
          text: `Check out this property: ${property.title}`,
          url: propertyUrl,
        });
        
        // Track share event
        console.log('Native share used', { propertyId: property.id, channel: 'native' });
      } catch (err) {
        // User cancelled or share failed
        console.log('Share cancelled or failed');
      }
    } else {
      // Fallback to copy link
      handleCopyLink();
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        onClick={handleWhatsAppShare}
        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
        data-testid="share-whatsapp-button"
      >
        <MessageCircle className="w-4 h-4" />
        <span className="hidden sm:inline">Chat on WhatsApp</span>
        <span className="sm:hidden">WhatsApp</span>
      </Button>

      <Button
        onClick={handleEmailShare}
        variant="outline"
        className="flex items-center gap-2"
        data-testid="share-email-button"
      >
        <Mail className="w-4 h-4" />
        <span className="hidden sm:inline">Email Agent</span>
        <span className="sm:hidden">Email</span>
      </Button>

      <Button
        onClick={handleCopyLink}
        variant="outline"
        className="flex items-center gap-2"
        data-testid="share-copy-link-button"
      >
        {copiedLink ? (
          <Check className="w-4 h-4 text-green-600" />
        ) : (
          <Link className="w-4 h-4" />
        )}
        <span className="hidden sm:inline">
          {copiedLink ? 'Copied!' : 'Copy Link'}
        </span>
        <span className="sm:hidden">
          {copiedLink ? 'Copied' : 'Link'}
        </span>
      </Button>

      {/* Generic share button for mobile devices with native sharing */}
      {navigator.share && (
        <Button
          onClick={handleGenericShare}
          variant="outline"
          className="flex items-center gap-2 sm:hidden"
          data-testid="share-native-button"
        >
          <Share2 className="w-4 h-4" />
          Share
        </Button>
      )}
    </div>
  );
}